'use client';

import { useEffect, useState, type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { useInView } from '@/lib/use-in-view';

/**
 * Scroll-reveal wrapper: fades + slides its (server-rendered) children up
 * once, on first entry into the viewport.
 *
 * Default — before hydration, and permanently if JS never runs — is fully
 * visible: the reveal classes are only added after `mounted` flips true in
 * an effect. Without this guard, a no-JS visitor would see this content
 * stuck at opacity-0 forever, which is strictly worse than no animation at
 * all.
 */
export function Reveal({
  children,
  className,
  delayMs = 0,
}: {
  children: ReactNode;
  className?: string;
  /** Staggers a grid of Reveal siblings (e.g. `i * 70`) into a cascade
   *  instead of all firing at once. Applied as `transition-delay`, so it
   *  only affects the reveal transition — never the inner card's own hover
   *  transitions (those live on a nested element, see features-section.tsx
   *  / pricing-section.tsx for the pattern). */
  delayMs?: number;
}) {
  const { ref, inView } = useInView<HTMLDivElement>();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  return (
    <div
      ref={ref}
      style={mounted && delayMs ? { transitionDelay: `${delayMs}ms` } : undefined}
      className={cn(
        mounted && 'transition duration-700 ease-out',
        mounted && (inView ? 'translate-y-0 opacity-100' : 'translate-y-3 opacity-0'),
        className,
      )}
    >
      {children}
    </div>
  );
}
